import { useState, useCallback, useEffect, useRef } from "react";
import {
  ScrollText,
  AlertTriangle,
  Pencil,
  RefreshCw,
  Activity,
  Users,
  Shield,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { KPICard } from "@/components/admin/shared/KPICard";
import { ExportButton } from "@/components/admin/shared/ExportButton";
import { DEBOUNCE_MS } from "./AuditLog/constants";
import {
  formatFullDate,
  getActionLabel,
  getResourceLabel,
} from "./AuditLog/formatters";
import { useDebouncedValue } from "./AuditLog/useDebouncedValue";
import { TimelineEntry } from "./AuditLog/TimelineEntry";
import { AuditFilters } from "./AuditLog/AuditFilters";
import { useAuditLogData } from "./AuditLog/useAuditLogData";

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AuditLog() {
  useAdminPageTitle("Journal d'activite");

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [resourceFilter, setResourceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userFilter, setUserFilter] = useState("");

  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  // Reset to page 1 when filters change
  const prevFiltersRef = useRef({
    debouncedSearch, actionFilter, resourceFilter, dateFrom, dateTo, userFilter,
  });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.debouncedSearch !== debouncedSearch ||
      prev.actionFilter !== actionFilter ||
      prev.resourceFilter !== resourceFilter ||
      prev.dateFrom !== dateFrom ||
      prev.dateTo !== dateTo ||
      prev.userFilter !== userFilter
    ) {
      setPage(1);
      prevFiltersRef.current = {
        debouncedSearch, actionFilter, resourceFilter, dateFrom, dateTo, userFilter,
      };
    }
  }, [debouncedSearch, actionFilter, resourceFilter, dateFrom, dateTo, userFilter]);

  const {
    isLoading,
    isError,
    usingMock,
    paginatedEntries,
    pagination,
    totalPages,
    kpis: { todayActions, activeUsers, modifications, deletions },
  } = useAuditLogData({
    page,
    debouncedSearch,
    actionFilter,
    resourceFilter,
    dateFrom,
    dateTo,
    userFilter,
  });

  // Export columns
  const exportColumns = [
    { key: "created_at", label: "Date" },
    { key: "user", label: "Utilisateur" },
    { key: "action", label: "Action" },
    { key: "resource", label: "Ressource" },
    { key: "resource_id", label: "ID Ressource" },
    { key: "ip", label: "Adresse IP" },
  ];

  const exportData = paginatedEntries.map((e) => ({
    created_at: formatFullDate(e.created_at),
    user: e.admin_user?.full_name ?? e.admin_user_id,
    action: getActionLabel(e.action),
    resource: getResourceLabel(e.resource),
    resource_id: e.resource_id ?? "",
    ip: ((e.metadata as Record<string, unknown> | null)?.ip as string) ?? "",
  }));

  const handlePrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((p) => Math.min(totalPages, p + 1));
  }, [totalPages]);

  const handleResetFilters = useCallback(() => {
    setSearchInput("");
    setActionFilter("");
    setResourceFilter("");
    setDateFrom("");
    setDateTo("");
    setUserFilter("");
    setPage(1);
  }, []);

  const hasActiveFilters = !!(searchInput || actionFilter || resourceFilter || dateFrom || dateTo || userFilter);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <ScrollText className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Journal d'activite
              </h1>
              <p className="text-sm text-stone-500">
                Historique complet des actions effectuees par les administrateurs.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-xs text-stone-400">
              <RefreshCw className="h-3 w-3" />
              Auto 30s
            </span>
            <ExportButton
              data={exportData}
              columns={exportColumns}
              filename="journal-activite"
            />
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Actions aujourd'hui"
            value={todayActions}
            icon={<Activity className="h-5 w-5" />}
          />
          <KPICard
            label="Utilisateurs actifs"
            value={activeUsers}
            icon={<Users className="h-5 w-5" />}
          />
          <KPICard
            label="Modifications"
            value={modifications}
            icon={<Pencil className="h-5 w-5" />}
          />
          <KPICard
            label="Alertes"
            value={deletions}
            icon={<Shield className="h-5 w-5" />}
          />
        </div>

        {/* Mock data notice */}
        {usingMock && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
            <p className="text-xs text-amber-700">
              Donnees de demonstration affichees. Les donnees reelles seront chargees automatiquement une fois l'API connectee.
            </p>
          </div>
        )}

        {/* Error banner */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              Impossible de charger le journal d'activite. Les donnees affichees
              peuvent etre obsoletes.
            </p>
          </div>
        )}

        {/* Filters */}
        <AuditFilters
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          actionFilter={actionFilter}
          onActionFilterChange={setActionFilter}
          resourceFilter={resourceFilter}
          onResourceFilterChange={setResourceFilter}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
          userFilter={userFilter}
          onUserFilterChange={setUserFilter}
          hasActiveFilters={hasActiveFilters}
          onReset={handleResetFilters}
        />

        {/* Loading state */}
        {isLoading && paginatedEntries.length === 0 && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-8 w-8 animate-pulse rounded-full bg-stone-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-stone-100" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-stone-100" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && paginatedEntries.length === 0 && (
          <div className="rounded-lg border border-stone-200 bg-white px-6 py-12 text-center">
            <ScrollText className="mx-auto h-8 w-8 text-stone-300" />
            <p className="mt-2 text-sm text-stone-500">
              Aucune entree dans le journal d'activite.
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="mt-3 text-sm font-medium text-amber-600 hover:text-amber-700"
              >
                Reinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* Timeline */}
        {paginatedEntries.length > 0 && (
          <div className="rounded-lg border border-stone-200 bg-white p-6">
            {paginatedEntries.map((entry) => (
              <TimelineEntry key={entry.id} entry={entry} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500">
              Page {pagination.page} sur {totalPages} ({pagination.total}{" "}
              entrees)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={page <= 1}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                Precedent
              </button>
              <button
                onClick={handleNextPage}
                disabled={page >= totalPages}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
